import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SecurityService } from '../../../security/security.service';
import { Book, BookManifest, Chapter, CurriculumManifest, Subject } from '../../../middle-school-math/models';

@Injectable({
  providedIn: 'root'
})
export class CurriculumAdminService {
  private endpoint = `${this.securityService.apiURL}/api/admin/curriculum`;

  constructor(
    private http: HttpClient,
    private securityService: SecurityService
  ) {}

  private getHeaders() {
    const token = this.securityService.getAuthToken();
    const caid = this.securityService.getCaid();
    return {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + token,
        caid: caid ? caid.toString() : '0'
      })
    };
  }

  // --- Curriculum Overview ---
  getCurriculum(): Observable<CurriculumManifest> {
    return this.http.get<CurriculumManifest>(this.endpoint, this.getHeaders());
  }

  saveCurriculum(manifest: CurriculumManifest): Observable<any> {
    return this.http.put<any>(this.endpoint, manifest, this.getHeaders());
  }

  // --- Subjects ---
  addSubject(subject: Subject): Observable<any> {
    return this.http.post<any>(`${this.endpoint}/subjects`, subject, this.getHeaders());
  }

  updateSubject(subjectId: string, subject: Subject): Observable<any> {
    return this.http.put<any>(`${this.endpoint}/subjects/${encodeURIComponent(subjectId)}`, subject, this.getHeaders());
  }

  deleteSubject(subjectId: string): Observable<any> {
    return this.http.delete<any>(`${this.endpoint}/subjects/${encodeURIComponent(subjectId)}`, this.getHeaders());
  }

  // --- Books ---
  addBook(book: Book): Observable<any> {
    return this.http.post<any>(`${this.endpoint}/books`, book, this.getHeaders());
  }

  updateBook(bookId: string, book: Book): Observable<any> {
    return this.http.put<any>(`${this.endpoint}/books/${encodeURIComponent(bookId)}`, book, this.getHeaders());
  }

  deleteBook(bookId: string): Observable<any> {
    return this.http.delete<any>(`${this.endpoint}/books/${encodeURIComponent(bookId)}`, this.getHeaders());
  }

  // --- Chapters per Book ---
  getBookChapters(bookId: string): Observable<BookManifest> {
    return this.http.get<BookManifest>(`${this.endpoint}/books/${encodeURIComponent(bookId)}/chapters`, this.getHeaders());
  }

  saveBookChapters(bookId: string, manifest: BookManifest): Observable<any> {
    return this.http.put<any>(`${this.endpoint}/books/${encodeURIComponent(bookId)}/chapters`, manifest, this.getHeaders());
  }

  addChapterToBook(bookId: string, chapter: Chapter): Observable<any> {
    return this.http.post<any>(`${this.endpoint}/books/${encodeURIComponent(bookId)}/chapters/chapter`, chapter, this.getHeaders());
  }

  updateChapterInBook(bookId: string, chapterId: string, chapter: Chapter): Observable<any> {
    return this.http.put<any>(`${this.endpoint}/books/${encodeURIComponent(bookId)}/chapters/chapter/${encodeURIComponent(chapterId)}`, chapter, this.getHeaders());
  }

  deleteChapterFromBook(bookId: string, chapterId: string): Observable<any> {
    return this.http.delete<any>(`${this.endpoint}/books/${encodeURIComponent(bookId)}/chapters/chapter/${encodeURIComponent(chapterId)}`, this.getHeaders());
  }

  // --- Courses Page Config ---
  getCoursesPageConfig(): Observable<any> {
    return this.http.get<any>(`${this.endpoint}/courses-page-config`, this.getHeaders());
  }

  saveCoursesPageConfig(config: any): Observable<any> {
    return this.http.put<any>(`${this.endpoint}/courses-page-config`, config, this.getHeaders());
  }
}
